<template>
  <v-container class="vpd-sub-content" fluid>
    <v-overlay :value="overlay" z-index="1000">
      <v-progress-circular indeterminate size="64" />
    </v-overlay>
    <vpd-account-summary :title="accounts[0] ? '' : 'Még nincs bankszámla csatlakoztatva'" subtitle="" :summaries="[]"
      :lastSynchronizationDate="''" @manualRefresh="manualRefresh" />
    <vpd-account-panel v-if="accounts[0]" :accounts="accounts" :config="config" @refreshUserData="refreshUserData">
    </vpd-account-panel>
    <div v-if="accounts[0]" class="vpd-account-actions">
      <v-btn class="primary huge px-4 vpd-account-actions__button" @click.stop="dialog = true">
        Új bankszámla csatlakoztatása
      </v-btn>
      <template v-if="isStatementProcessorEnabled">
        <span class="vpd-account-actions__separator">vagy</span>
        <v-btn class="primary huge px-4 vpd-account-actions__button" :to="{ name: 'pdf-upload' }">
          PDF Bankszámlakivonat feltöltése
        </v-btn>
      </template>
    </div>
    <div v-if="!accounts[0]" class="vpd-account-summary py-5">
      <div class="vpd-account-actions">
        <v-btn class="primary huge px-4 vpd-account-actions__button" @click.stop="dialog = true">
          Új bankszámla csatlakoztatása
        </v-btn>
        <template v-if="isStatementProcessorEnabled">
          <span class="vpd-account-actions__separator">vagy</span>
          <v-btn class="primary huge px-4 vpd-account-actions__button" :to="{ name: 'pdf-upload' }">
            PDF Bankszámlakivonat feltöltése
          </v-btn>
        </template>
      </div>
    </div>

    <v-dialog v-model="dialog" content-class="v-dialog--custom">
      <vpd-banklist-panel v-on:close-dialog="closeDialog" :config="config" :active.sync="dialog" />
    </v-dialog>

    <div v-if="accounts[0]" class="vpd-account-select-wrap pt-4 pb-2">
      <v-select v-model="selectedAccountUUID" :items="accountOptions" item-text="text" item-value="value" label="Számla"
        dense hide-details class="vpd-account-select" :menu-props="{ contentClass: 'vpd-account-select-menu' }"
        @change="onAccountChange" />
    </div>

    <section
      v-if="accounts[0] && isAccountExport"
      class="vpd-export-controls my-4"
      aria-labelledby="export-heading"
    >
      <h2
        id="export-heading"
        class="subtitle-1 font-weight-bold vpd-export-controls__title"
      >
        Számla- és tranzakcióadatok exportálása
      </h2>
      <p class="body-2 vpd-export-controls__description">
        Válasszon ki legfeljebb 12 egymást követő hónapot. Az export
        CSV-fájlként töltődik le.
      </p>
      <div class="vpd-export-controls__fields">
        <v-select
          v-model="selectedExportAccountUUID"
          :items="accountOptions"
          item-text="text"
          item-value="value"
          label="Számla"
          outlined
          dense
          hide-details
          class="vpd-export-controls__field"
        />
        <v-menu
          v-model="exportFromMonthMenu"
          :close-on-content-click="false"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="exportFromMonth"
              v-bind="attrs"
              label="Kezdő hónap"
              append-icon="mdi-calendar-month"
              outlined
              dense
              hide-details
              readonly
              class="vpd-export-controls__field"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="exportFromMonth"
            type="month"
            locale="hu-HU"
            no-title
            @input="exportFromMonthMenu = false"
          />
        </v-menu>
        <v-menu
          v-model="exportToMonthMenu"
          :close-on-content-click="false"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="exportToMonth"
              v-bind="attrs"
              label="Záró hónap"
              append-icon="mdi-calendar-month"
              outlined
              dense
              readonly
              :error-messages="exportRangeError ? [exportRangeError] : []"
              class="vpd-export-controls__field"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="exportToMonth"
            type="month"
            locale="hu-HU"
            no-title
            @input="exportToMonthMenu = false"
          />
        </v-menu>
        <v-btn
          class="primary vpd-export-controls__button"
          :loading="exportLoading"
          :disabled="exportLoading || !exportRangeValid"
          @click="exportAccountsCsv"
        >
          <v-icon left>mdi-download</v-icon>
          CSV letöltése
        </v-btn>
      </div>
    </section>

    <vpd-transactions v-if="accounts[0]" :key="selectedAccountUUID" @jump="jumpPage" justify="space-between"
      :title="transactionsTitle" :length="transactionsLength" :transactions="transactions"
      :loading="loadingTransactions" :page="transactionsPage" :showFootPrint="showFootPrint" />
  </v-container>
</template>

<script>
import VpdAccountSummary from "@/components/vpd-account-summary/vpd-account-summary";
import VpdTransactions from "@/components/vpd-transactions/vpd-transactions";
import VpdAccountPanel from "@/components/vpd-account-panel/vpd-account-panel";
import VpdBanklistPanel from "@/components/vpd-banklist-panel/vpd-banklist-panel";
import accountsService from "@/services/accounts-service";
import transactionsService from "@/services/transactions-service";
import { getFormattedAccountNumber } from "@/modules/helpers";
import { normalizeExportMonth } from "@/modules/export-month";
import VpdEventBus from "../modules/vpd-event-bus";

const ALL_ACCOUNTS = "all";

export default {
  data() {
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(
      now.getMonth() + 1
    ).padStart(2, "0")}`;
    return {
      dialog: false,
      loadingTransactions: false,
      loadingAccounts: false,
      exportLoading: false,
      summaries: [],
      transactions: [],
      accounts: [],
      accountNumbers: [],
      transactionsTotalCount: null,
      transactionsItemsPerPage: 15,
      transactionsPage: 1,
      lastEvaluatedAccountKey: null,
      transactionsLength: 1,
      overlay: false,
      lastSynchronizationDate: "",
      showFootPrint: "",
      selectedAccountUUID: ALL_ACCOUNTS,
      selectedExportAccountUUID: ALL_ACCOUNTS,
      exportFromMonth: currentMonth,
      exportToMonth: currentMonth,
      exportFromMonthMenu: false,
      exportToMonthMenu: false,
    };
  },
  name: "home",
  components: {
    VpdBanklistPanel,
    VpdAccountPanel,
    VpdTransactions,
    VpdAccountSummary,
  },
  props: ["config"],
  computed: {
    isAccountExport: function () {
      return this.$store.getters["general/accountExportEnabled"];
    },
    isStatementProcessorEnabled: function () {
      return this.$store.getters["general/statementProcessorEnabled"];
    },
    accountOptions: function () {
      const options = this.accounts
        .map((account) => ({
          text: this.accountLabel(account),
          value: this.accountValue(account),
        }))
        .sort((a, b) =>
          a.text.replace(/[\s-]/g, "").localeCompare(
            b.text.replace(/[\s-]/g, ""),
            "hu",
            { numeric: true }
          )
        );
      return [{ text: "Összes", value: ALL_ACCOUNTS }, ...options];
    },
    transactionsTitle: function () {
      if (this.selectedAccountUUID === ALL_ACCOUNTS)
        return "Összesített tranzakciólista";
      const selected = this.accountOptions.find(
        (o) => o.value === this.selectedAccountUUID
      );
      return selected ? selected.text : "Tranzakciólista";
    },
    exportRangeError: function () {
      const normalizedFromMonth = normalizeExportMonth(this.exportFromMonth);
      const normalizedToMonth = normalizeExportMonth(this.exportToMonth);

      if (!normalizedFromMonth || !normalizedToMonth) {
        return "Mindkét hónap megadása kötelező.";
      }

      const [fromYear, fromMonth] = normalizedFromMonth.split("-").map(Number);
      const [toYear, toMonth] = normalizedToMonth.split("-").map(Number);
      const inclusiveMonthCount =
        (toYear - fromYear) * 12 + toMonth - fromMonth + 1;

      if (inclusiveMonthCount < 1) {
        return "A záró hónap nem előzheti meg a kezdő hónapot.";
      }
      if (inclusiveMonthCount > 12) {
        return "Legfeljebb 12 hónapos időszak exportálható.";
      }

      return "";
    },
    exportRangeValid: function () {
      return !this.exportRangeError;
    },
  },
  watch: {
    // if the selected account disappears after a refresh, fall back to "Összes"
    accounts: function () {
      const stillExists = this.accountOptions.some(
        (o) => o.value === this.selectedAccountUUID
      );
      if (!stillExists) this.selectedAccountUUID = ALL_ACCOUNTS;

      const exportAccountStillExists = this.accountOptions.some(
        (option) => option.value === this.selectedExportAccountUUID
      );
      if (!exportAccountStillExists)
        this.selectedExportAccountUUID = ALL_ACCOUNTS;
    },
  },
  methods: {
    accountValue: function (account) {
      return account.id;
    },
    accountLabel: function (account) {
      const bban = account.bban || account.BBAN || account.accountNumber;
      return bban
        ? getFormattedAccountNumber(bban)
        : this.accountValue(account);
    },
    parseContentDispositionFileName: function (contentDisposition) {
      if (!contentDisposition) {
        return null;
      }

      const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);

      if (utf8Match && utf8Match[1]) {
        return decodeURIComponent(utf8Match[1]);
      }

      const plainMatch = contentDisposition.match(/filename="?([^"]+)"?/i);

      return plainMatch && plainMatch[1] ? plainMatch[1] : null;
    },
    normalizeExportResponse: async function (response) {
      const fallbackFileName = "accounts-and-transactions.csv";
      const fallbackContentType = "text/csv;charset=utf-8;";
      const responseBlob = response?.data;
      const responseHeaders = response?.headers || {};
      const responseText = await responseBlob.text();

      try {
        const parsed = JSON.parse(responseText);

        if (parsed && typeof parsed.body === "string") {
          const fileName =
            this.parseContentDispositionFileName(
              parsed.headers?.["Content-Disposition"] ||
              parsed.headers?.["content-disposition"]
            ) || fallbackFileName;
          const contentType =
            parsed.headers?.["Content-Type"] ||
            parsed.headers?.["content-type"] ||
            fallbackContentType;

          return {
            fileName,
            blob: new Blob([parsed.body], { type: contentType }),
          };
        }
      } catch (error) {
        // Raw CSV response, not a JSON envelope.
      }

      return {
        fileName:
          this.parseContentDispositionFileName(
            responseHeaders["content-disposition"]
          ) || fallbackFileName,
        blob: new Blob([responseText], {
          type: responseBlob.type || fallbackContentType,
        }),
      };
    },
    fetchTransactionsForSelection: async function (skip) {
      if (this.selectedAccountUUID === ALL_ACCOUNTS) {
        return await transactionsService.fetchTransactions(
          skip,
          this.transactionsItemsPerPage
        );
      }
      return await transactionsService.fetchAccountTransactions(
        this.selectedAccountUUID,
        skip,
        this.transactionsItemsPerPage
      );
    },
    jumpPage: async function (page) {
      if (
        this.loadingTransactions ||
        page < 1 ||
        page > this.transactionsLength
      )
        return;

      const skip = (page - 1) * this.transactionsItemsPerPage;
      this.loadingTransactions = true;

      try {
        const transactionsData = await this.fetchTransactionsForSelection(skip);
        if (transactionsData) this.loadTransactionsData(transactionsData, page);
      } catch (error) {
        // Keep the last successfully loaded page and its transactions visible.
      } finally {
        this.loadingTransactions = false;
      }
    },
    onAccountChange: async function () {
      this.setLoading(true);
      const transactionsData = await this.fetchTransactionsForSelection(0);
      if (transactionsData && transactionsData.totalCount > 0) {
        this.loadTransactionsData(transactionsData);
      } else {
        this.transactions = [];
        this.transactionsLength = 1;
        this.transactionsPage = 1;
      }
      this.setLoading(false);
    },
    loadAccountsData: function (data) {
      this.accounts = data.items.filter(
        (account) =>
          account.isValid.toLowerCase() === "true" || account.deletedAt
      );
      this.summaries = data.summaries;
      this.lastSynchronizationDate = data.lastSynchronizationDate.substring(
        0,
        16
      );
      this.showFootPrint = data.showFootPrint;
      this.$store.commit("setAccountsCount", this.accounts.length);
    },
    loadTransactionsData: function (data, page = 1) {
      let pageNumber = data.totalCount / this.transactionsItemsPerPage;
      this.transactionsLength = Math.ceil(pageNumber);
      this.transactions = data.items;
      this.transactionsPage = page;
    },
    setLoading: function (value) {
      this.overlay = value;
      this.loadingAccounts = value;
      this.loadingTransactions = value;
    },
    closeDialog: function () {
      this.dialog = false;
    },
    refreshAccounts: async function () {
      const accountsData = await accountsService.fetchAccounts();
      if (accountsData) this.loadAccountsData(accountsData);
    },
    refreshTransactions: async function () {
      const transactionsData = await this.fetchTransactionsForSelection(0);
      if (transactionsData && transactionsData.totalCount > 0)
        this.loadTransactionsData(transactionsData);
    },
    refreshUserData: async function () {
      await this.refreshAccounts();
      await this.refreshTransactions();
    },
    manualRefresh: async function () {
      this.setLoading(true);
      await accountsService.refreshAccounts();
      this.setLoading(false);

      await this.refreshUserData();
    },
    exportAccountsCsv: async function () {
      if (!this.isAccountExport || !this.exportRangeValid) return;

      this.exportLoading = true;

      try {
        const accountId =
          this.selectedExportAccountUUID === ALL_ACCOUNTS
            ? undefined
            : this.selectedExportAccountUUID;
        const exportResponse = await accountsService.exportAccounts({
          accountId,
          fromMonth: this.exportFromMonth,
          toMonth: this.exportToMonth,
        });

        if (!exportResponse?.data) {
          return;
        }

        const normalizedResponse = await this.normalizeExportResponse(
          exportResponse
        );
        const blobUrl = window.URL.createObjectURL(normalizedResponse.blob);
        const link = document.createElement("a");

        link.href = blobUrl;
        link.setAttribute("download", normalizedResponse.fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);

        this.$notify({
          group: "success",
          title: "Értesítés",
          text: "A számla- és tranzakcióadatok exportja elkészült.",
        });
      } finally {
        this.exportLoading = false;
      }
    },
  },
  async created() {
    VpdEventBus.$on("refresh-account", this.refreshAccounts);
    VpdEventBus.$on("refresh-transaction", this.refreshTransactions);

    this.$store.commit("setAccountsCount", 0);
    this.setLoading(true);

    await this.refreshAccounts();
    await this.refreshTransactions();

    this.setLoading(false);
  },
  destroyed() {
    VpdEventBus.$off("refresh-account", this.refreshAccounts);
    VpdEventBus.$off("refresh-transaction", this.refreshTransactions);
  },
};
</script>

<style scoped>
.vpd-account-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 22px 28px;
}

.vpd-account-actions__button {
  width: 304px;
  max-width: 100%;
}

.vpd-account-actions__separator {
  flex: 0 0 auto;
}

.vpd-account-select {
  max-width: 320px;
  margin-left: auto;
  /* desktop: right aligned */
}

/* mobile: full width, left aligned */
@media (max-width: 959px) {
  .vpd-account-select {
    max-width: none;
    margin-left: 0;
    padding-left: 0 !important;
  }
}
</style>

<style>
/* the dropdown menu renders detached (appended to body), so scoped styles
   can't reach it — this block is intentionally unscoped */
.vpd-account-select-menu .v-list-item {
  padding: 8px !important;
  min-height: 48px;
}

.vpd-export-controls {
  padding: 20px;
  border: 1px solid #dedede;
  border-radius: 4px;
}

.vpd-export-controls__title,
.vpd-export-controls__description {
  margin-bottom: 12px;
}

.vpd-export-controls__fields {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  flex-wrap: nowrap;
}

.vpd-export-controls__field {
  flex: 1 1 0;
  min-width: 0;
  max-width: 260px;
}

.vpd-export-controls__button {
  flex: 0 0 auto;
  height: 40px !important;
  min-width: 180px;
  margin-top: 0;
}

@media (max-width: 959px) {
  .vpd-export-controls__fields {
    display: block;
  }

  .vpd-export-controls__field,
  .vpd-export-controls__button {
    max-width: none;
    width: 100%;
  }

  .vpd-export-controls__field {
    margin-bottom: 12px;
  }

  .vpd-export-controls__button {
    margin-top: 0;
  }
}
</style>
